/**
 * Alta de una cuenta, en ventana.  (061 · FR-002)
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from 'react-router'

import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import { api, ErrorApi } from '../../lib/api'
import { RejillaNegocios, SelectorAcceso, type NegocioDir } from './usuario-piezas'

export function CuerpoUsuarioNuevo({
    negocios,
    onCerrar,
}: {
    negocios: NegocioDir[]
    onCerrar: () => void
}) {
    const nav = useNavigate()
    const qc = useQueryClient()
    const [nombre, setNombre] = useState('')
    const [email, setEmail] = useState('')
    const [soloLectura, setSoloLectura] = useState(false)
    const [elegidos, setElegidos] = useState<number[]>([])
    const [aviso, setAviso] = useState<string | null>(null)
    const [enlace, setEnlace] = useState<string | null>(null)

    const crear = useMutation({
        mutationFn: () =>
            api.post<{
                id: number
                email: string
                inviteUrl: string
                correoEnviado: boolean
            }>('/usuarios', {
                email,
                nombre,
                role: 'cliente',
                accountIds: elegidos,
                soloLectura,
            }),
        onSuccess: (r) => {
            void qc.invalidateQueries({ queryKey: ['usuarios'] })
            if (r.correoEnviado) {
                nav(`/usuarios/${r.id}`)
                return
            }
            setAviso('Creado. El correo no salió: copia el enlace.')
            setEnlace(r.inviteUrl)
        },
        onError: (e: Error) => {
            setEnlace(null)
            setAviso(e instanceof ErrorApi ? e.message : 'No se pudo crear.')
        },
    })

    const listo = nombre.trim().length > 0 && email.includes('@') && elegidos.length > 0

    return (
        <form
            className="space-y-6"
            data-testid="usuario-nuevo"
            onSubmit={(e) => {
                e.preventDefault()
                if (!listo) return
                crear.mutate()
            }}
        >
            <p className="text-sm text-neutral-400">
                Nombre, correo y a qué negocios entra. Él elige la contraseña.
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                    <Label htmlFor="nombre-usuario">Nombre</Label>
                    <Input
                        id="nombre-usuario"
                        value={nombre}
                        onChange={(e) => setNombre(e.target.value)}
                        placeholder="Ana Pérez"
                        autoComplete="name"
                    />
                </div>
                <div className="space-y-1.5">
                    <Label htmlFor="correo-usuario">Correo</Label>
                    <Input
                        id="correo-usuario"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="ana@negocio.es"
                        autoComplete="email"
                    />
                </div>
            </div>

            <div className="space-y-2">
                <p className="text-xs font-medium text-neutral-400">Cómo entra</p>
                <SelectorAcceso soloLectura={soloLectura} onChange={setSoloLectura} />
            </div>

            <div className="space-y-2">
                <p className="text-xs font-medium text-neutral-400">Negocios</p>
                <RejillaNegocios
                    negocios={negocios}
                    elegidos={elegidos}
                    onToggle={(id) =>
                        setElegidos((prev) =>
                            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
                        )
                    }
                />
            </div>

            {aviso && (
                <p role="status" className="text-sm text-neutral-300">
                    {aviso}
                </p>
            )}
            {enlace && (
                <p className="break-all text-xs text-sky-400">
                    <a href={enlace} className="underline">
                        {enlace}
                    </a>
                </p>
            )}

            <div className="flex flex-wrap gap-2">
                <Button type="submit" disabled={crear.isPending || !listo}>
                    {crear.isPending ? 'Creando…' : 'Crear e invitar'}
                </Button>
                <Button type="button" variant="ghost" onClick={onCerrar}>
                    Cancelar
                </Button>
            </div>
        </form>
    )
}
